import { BtlApplicationFormPageEnum } from '@pages/BtlApplicationFormPage/types';
import { CaseSidebarItemId } from '../types';

/**
 * Submit application and Additional information share the same form page in the new sidebar.
 * Submit DIP uses its own form with DIP-specific assertions.
 */
export const CASE_SIDEBAR_SUBMISSION_PAGE_ITEMS: CaseSidebarItemId[] = [
  CaseSidebarItemId.SUBMIT_APPLICATION,
  CaseSidebarItemId.ADDITIONAL_INFORMATION,
];

const CASE_SIDEBAR_ITEM_TO_FORM_SECTION: Partial<Record<CaseSidebarItemId, BtlApplicationFormPageEnum>> = {
  [CaseSidebarItemId.OVERVIEW]: BtlApplicationFormPageEnum.CASE_SUMMARY,
  [CaseSidebarItemId.CUSTOMER_FILES]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.EVIDENCE]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.FINANCING]: BtlApplicationFormPageEnum.FINANCING,
  [CaseSidebarItemId.APPLICANTS]: BtlApplicationFormPageEnum.APPLICANTS,
  [CaseSidebarItemId.EXPENSES]: BtlApplicationFormPageEnum.EXPENSES,
  [CaseSidebarItemId.PRODUCT]: BtlApplicationFormPageEnum.PRODUCT,
  [CaseSidebarItemId.PROPERTY]: BtlApplicationFormPageEnum.PROPERTY,
  [CaseSidebarItemId.AFFORDABILITY_CHECK]: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
  [CaseSidebarItemId.TOP_SLICING]: BtlApplicationFormPageEnum.TOP_SLICING,
  [CaseSidebarItemId.SUBMIT_DIP]: BtlApplicationFormPageEnum.SUBMIT_DIP,
  [CaseSidebarItemId.SOLICITORS]: BtlApplicationFormPageEnum.SOLICITORS,
  [CaseSidebarItemId.BANK_DETAILS]: BtlApplicationFormPageEnum.BANK_DETAILS,
  [CaseSidebarItemId.SUBMIT_APPLICATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
  [CaseSidebarItemId.VALUATION]: BtlApplicationFormPageEnum.VALUATION,
  [CaseSidebarItemId.POST_OFFER]: BtlApplicationFormPageEnum.POST_OFFER,
  [CaseSidebarItemId.COMMUNICATION]: BtlApplicationFormPageEnum.COMMUNICATION,
  [CaseSidebarItemId.ADDITIONAL_INFORMATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
};

/** Old URL section params that should resolve to a CaseSidebar item (bookmarks / deep links). */
const URL_ALIAS_TO_CASE_SIDEBAR_ITEM: Partial<Record<BtlApplicationFormPageEnum, CaseSidebarItemId>> = {
  [BtlApplicationFormPageEnum.CASE_SUMMARY]: CaseSidebarItemId.OVERVIEW,
  [BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY]: CaseSidebarItemId.FINANCING,
  [BtlApplicationFormPageEnum.DOCUMENTS]: CaseSidebarItemId.CUSTOMER_FILES,
};

/** Child form routes that share a parent sidebar item — used for active state only. */
const NESTED_FORM_SECTION_TO_CASE_SIDEBAR_ITEM: Partial<Record<BtlApplicationFormPageEnum, CaseSidebarItemId>> = {
  [BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL]: CaseSidebarItemId.APPLICANTS,
  [BtlApplicationFormPageEnum.APPLICANTS_COMPANY]: CaseSidebarItemId.APPLICANTS,
  [BtlApplicationFormPageEnum.TOP_SLICING_HOUSEHOLD]: CaseSidebarItemId.TOP_SLICING,
};

const CASE_SIDEBAR_ITEM_IDS = new Set<string>(Object.values(CaseSidebarItemId));

export function isCaseSidebarItemId(value: string): value is CaseSidebarItemId {
  return CASE_SIDEBAR_ITEM_IDS.has(value);
}

export function isNestedFormSection(section?: string | null): section is BtlApplicationFormPageEnum {
  if (!section) {
    return false;
  }

  return Boolean(NESTED_FORM_SECTION_TO_CASE_SIDEBAR_ITEM[section as BtlApplicationFormPageEnum]);
}

export function mapCaseSidebarItemToFormSection(itemId: CaseSidebarItemId): BtlApplicationFormPageEnum | undefined {
  return CASE_SIDEBAR_ITEM_TO_FORM_SECTION[itemId];
}

export function mapCaseSidebarItemToUrlSection(itemId: CaseSidebarItemId): string {
  if (itemId === CaseSidebarItemId.OVERVIEW) {
    return BtlApplicationFormPageEnum.CASE_SUMMARY;
  }

  return itemId;
}

export function resolveCaseSidebarItemId(section?: string | null): CaseSidebarItemId | undefined {
  if (!section) {
    return undefined;
  }

  if (section === BtlApplicationFormPageEnum.CASE_SUMMARY) {
    return CaseSidebarItemId.OVERVIEW;
  }

  if (isCaseSidebarItemId(section)) {
    return section;
  }

  const nestedSidebarItemId = NESTED_FORM_SECTION_TO_CASE_SIDEBAR_ITEM[section as BtlApplicationFormPageEnum];

  if (nestedSidebarItemId) {
    return nestedSidebarItemId;
  }

  return URL_ALIAS_TO_CASE_SIDEBAR_ITEM[section as BtlApplicationFormPageEnum];
}

export function resolveFormSection(section?: string | null): BtlApplicationFormPageEnum | undefined {
  if (!section) {
    return undefined;
  }

  if (NESTED_FORM_SECTION_TO_CASE_SIDEBAR_ITEM[section as BtlApplicationFormPageEnum]) {
    return section as BtlApplicationFormPageEnum;
  }

  const caseSidebarItemId = resolveCaseSidebarItemId(section);

  if (caseSidebarItemId) {
    const formSection = mapCaseSidebarItemToFormSection(caseSidebarItemId);

    if (formSection) {
      return formSection;
    }
  }

  return section as BtlApplicationFormPageEnum;
}
